/**
 * All Actions Modal Component
 * 
 * Displays AI agent actions relevant to the current page context.
 * Includes search functionality and category filtering.
 */
import { useState, useMemo } from "react";
import { Search, X, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ALL_ACTION_CATEGORIES,
  type ActionCategory,
} from "./allActionsConfig";
import type { QuickAction } from "./QuickActionCard";

interface AllActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionSelect: (action: QuickAction) => void;
  /** Page name for display */
  pageName: string;
  /** Category IDs relevant to the current page context */
  relevantCategories: string[];
}

export const AllActionsModal = ({
  isOpen,
  onClose,
  onActionSelect,
  pageName,
  relevantCategories,
}: AllActionsModalProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter categories to only show relevant ones for this page
  const filteredCategories = useMemo(() => {
    return ALL_ACTION_CATEGORIES.filter((cat) =>
      relevantCategories.includes(cat.id)
    );
  }, [relevantCategories]);

  // Get all actions from relevant categories
  const relevantActions = useMemo(() => {
    return filteredCategories.flatMap((cat) => cat.actions);
  }, [filteredCategories]);

  const totalActions = relevantActions.length;

  // Filter actions based on search query (only within relevant categories)
  const searchFilteredActions = useMemo(() => {
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      return relevantActions.filter(
        (action) =>
          action.title.toLowerCase().includes(lowerQuery) ||
          action.description.toLowerCase().includes(lowerQuery)
      );
    }
    return null; // Return null to show categories
  }, [searchQuery, relevantActions]);

  // Get actions for selected category
  const categoryActions = useMemo(() => {
    if (selectedCategory) {
      const category = filteredCategories.find(
        (c) => c.id === selectedCategory
      );
      return category?.actions || [];
    }
    return [];
  }, [selectedCategory, filteredCategories]);

  const handleActionClick = (action: QuickAction) => {
    onActionSelect(action);
    onClose();
    setSearchQuery("");
    setSelectedCategory(null);
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  const handleClose = () => {
    onClose();
    setSearchQuery("");
    setSelectedCategory(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] max-h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b border-gray6">
          <DialogTitle className="text-base font-semibold text-black1">
            {selectedCategory
              ? filteredCategories.find((c) => c.id === selectedCategory)?.name
              : `${pageName} Actions`}
          </DialogTitle>
          <p className="text-xs text-gray4 mt-0.5">
            {selectedCategory
              ? `${categoryActions.length} actions available`
              : `${totalActions} actions across ${filteredCategories.length} categories`}
          </p>
        </DialogHeader>

        {/* Search Bar */}
        <div className="px-4 py-2.5 border-b border-gray6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedCategory(null);
              }}
              placeholder="Search actions..."
              className={cn(
                "w-full pl-9 pr-8 py-2 text-xs rounded-md",
                "border border-gray6 bg-gray7",
                "focus:outline-none focus:ring-1 focus:ring-greentab focus:border-greentab",
                "placeholder:text-gray4"
              )}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray4 hover:text-gray2"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Search Results */}
          {searchQuery.trim() && searchFilteredActions && (
            <div className="p-3">
              {searchFilteredActions.length === 0 ? (
                <div className="text-center py-6 text-gray4 text-xs">
                  No actions found for "{searchQuery}"
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-[10px] text-gray4 uppercase tracking-wide mb-2">
                    {searchFilteredActions.length} results
                  </p>
                  {searchFilteredActions.map((action) => (
                    <ActionItem
                      key={action.id}
                      action={action}
                      onClick={handleActionClick}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Category Actions */}
          {!searchQuery.trim() && selectedCategory && (
            <div className="p-3">
              {filteredCategories.length > 1 && (
                <button
                  onClick={handleBackToCategories}
                  className="flex items-center gap-1.5 text-xs text-greentab hover:text-green2 mb-3"
                >
                  <ChevronRight className="h-3 w-3 rotate-180" />
                  Back to categories
                </button>
              )}
              <div className="space-y-1">
                {categoryActions.map((action) => (
                  <ActionItem
                    key={action.id}
                    action={action}
                    onClick={handleActionClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Category List - show if multiple categories, otherwise show actions directly */}
          {!searchQuery.trim() && !selectedCategory && (
            <div className="p-3 space-y-1">
              {filteredCategories.length === 1 ? (
                // Single category - show actions directly
                filteredCategories[0].actions.map((action) => (
                  <ActionItem
                    key={action.id}
                    action={action}
                    onClick={handleActionClick}
                  />
                ))
              ) : (
                // Multiple categories - show category list
                filteredCategories.map((category) => (
                  <CategoryItem
                    key={category.id}
                    category={category}
                    onClick={handleCategoryClick}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Category Item Component
 */
interface CategoryItemProps {
  category: ActionCategory;
  onClick: (categoryId: string) => void;
}

const CategoryItem = ({ category, onClick }: CategoryItemProps) => {
  const Icon = category.icon;

  return (
    <button
      onClick={() => onClick(category.id)}
      className={cn(
        "w-full px-3 py-2.5 rounded-md border border-gray6 bg-white text-left",
        "hover:border-greentab hover:bg-green4 transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-greentab focus:ring-offset-1",
        "group flex items-center justify-between"
      )}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            "h-7 w-7 rounded border border-gray5 bg-white flex items-center justify-center",
            "group-hover:border-greentab group-hover:bg-green4 transition-colors duration-200"
          )}
        >
          <Icon
            className={cn(
              "h-3.5 w-3.5 text-gray3",
              "group-hover:text-green2 transition-colors duration-200"
            )}
          />
        </div>
        <div>
          <h4 className="font-medium text-black1 text-xs">{category.name}</h4>
          <p className="text-gray4 text-[10px]">
            {category.actions.length} actions
          </p>
        </div>
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-gray5 group-hover:text-greentab" />
    </button>
  );
};

/**
 * Action Item Component
 */
interface ActionItemProps {
  action: QuickAction;
  onClick: (action: QuickAction) => void;
}

const ActionItem = ({ action, onClick }: ActionItemProps) => {
  const Icon = action.icon;

  return (
    <button
      onClick={() => onClick(action)}
      className={cn(
        "w-full px-2.5 py-2 rounded-md border border-gray6 bg-white text-left",
        "hover:border-greentab hover:bg-green4 transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-greentab focus:ring-offset-1",
        "group flex items-center gap-2"
      )}
    >
      <div
        className={cn(
          "h-6 w-6 rounded border border-gray5 bg-white flex items-center justify-center flex-shrink-0",
          "group-hover:border-greentab group-hover:bg-green4 transition-colors duration-200"
        )}
      >
        <Icon
          className={cn(
            "h-3 w-3 text-gray3",
            "group-hover:text-green2 transition-colors duration-200"
          )}
        />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="font-medium text-black1 text-xs leading-tight truncate">
          {action.title}
        </h4>
        <p className="text-gray4 text-[10px] leading-tight truncate">
          {action.description}
        </p>
      </div>
      <ChevronRight className="h-3 w-3 text-gray5 group-hover:text-greentab flex-shrink-0" />
    </button>
  );
};

